import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, Calendar, Filter, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatPrice, getTrackColorClasses } from '../../lib/utils';

interface Booking {
  id: string;
  trackTypeId: string;
  trackName: string;
  trackColor: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  requestNotes?: string;
  adminNotes?: string;
  bookedByName: string;
  createdAt: string;
  pricePerSlot: number;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'requested', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function TeamBookings() {
  const { slug } = useParams<{ slug: string }>();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [userRole, setUserRole] = useState<string>('member');

  useEffect(() => {
    fetchBookings();
  }, [slug, statusFilter]);

  async function fetchBookings() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const res = await api.get(`/api/teams/${slug}/bookings?${params.toString()}`);
      if (res.bookings) {
        setBookings(res.bookings);
      }
      if (res.userRole) {
        setUserRole(res.userRole);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  }

  const canBook = ['owner', 'admin', 'manager'].includes(userRole);

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      requested: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <p className="text-gray-600 mt-1">
            View and manage your team's track bookings.
          </p>
        </div>
        {canBook && (
          <Link to={`/team/${slug}/bookings/new`} className="btn btn-primary">
            <Plus size={18} className="mr-2" />
            New Booking
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-400" />
            <span className="text-sm text-gray-600">Filter:</span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input input-sm"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No bookings found
          </h2>
          <p className="text-gray-600 mb-6">
            {statusFilter
              ? 'No bookings match your current filter.'
              : "Your team hasn't made any bookings yet."}
          </p>
          {canBook && !statusFilter && (
            <Link to={`/team/${slug}/bookings/new`} className="btn btn-primary">
              <Plus size={18} className="mr-2" />
              Make Your First Booking
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => {
            const trackColors = getTrackColorClasses(booking.trackColor);

            return (
              <div key={booking.id} className="card overflow-hidden">
                <div className={`h-1 ${trackColors.bg}`} />
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-gray-900">
                          {booking.trackName}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                            booking.status
                          )}`}
                        >
                          {booking.status}
                        </span>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {formatDate(booking.bookingDate)}
                        </p>
                        <p>
                          <span className="font-medium">Time:</span>{' '}
                          {booking.startTime} - {booking.endTime}
                        </p>
                        <p>
                          <span className="font-medium">Booked by:</span>{' '}
                          {booking.bookedByName}
                        </p>
                      </div>
                      {booking.requestNotes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Notes:</span>{' '}
                            {booking.requestNotes}
                          </p>
                        </div>
                      )}
                      {booking.adminNotes && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Admin response:</span>{' '}
                            {booking.adminNotes}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-900">
                        {formatPrice(booking.pricePerSlot)}
                      </div>
                      <div className="text-sm text-gray-500">per slot</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
